// Origineel: client/imports/ui/Tasks/TaskState.tsx en Tasks/SharedTasks/SharedTaskState.tsx
/**
 * De gedeelde staat van de takenschermen, om de hele app heen.
 *
 * In het origineel zet `index.tsx` `TaskStateProvider` (`value={[]}`) en
 * `SharedTaskStateProvider` (`value={{ showItems: false, allTasksPerPlayer: [], allTasks: [] }}`)
 * om de router, zodat de gekozen taken bewaard blijven bij het wisselen van scherm. De router
 * mount deze component boven alle routes.
 */
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
  type SetStateAction,
} from 'react';
import { bewaarKeuze, leesKeuze } from './eigenKeuze';
import { SharedTaskStateContext, type SharedTaskStateData } from './SharedTaskState';
import { TaskStateContext } from './TaskState';

/** De eigen keuze met de sleutel waaronder hij in de browser staat (zie `eigenKeuze.ts`). */
type EigenKeuze = { sleutel?: string; taken: string[] };

export function TaskStateProvider({ children, value }: { children: ReactNode; value: string[] }) {
  // Sleutel en keuze in één staat: zo komt een keuze nooit onder de sleutel van een andere week.
  const [keuze, setKeuze] = useState<EigenKeuze>({ taken: value });
  const setSelectedTasks = useCallback((nieuw: SetStateAction<string[]>) => {
    setKeuze((vorige) => {
      const taken = typeof nieuw === 'function' ? nieuw(vorige.taken) : nieuw;
      return taken === vorige.taken ? vorige : { ...vorige, taken };
    });
  }, []);
  const bewaarOnder = useCallback((sleutel: string) => {
    setKeuze((vorige) =>
      vorige.sleutel === sleutel ? vorige : { sleutel, taken: leesKeuze(sleutel) },
    );
  }, []);
  useEffect(() => {
    if (keuze.sleutel) bewaarKeuze(keuze.sleutel, keuze.taken);
  }, [keuze]);
  const selectedTasks = keuze.taken;
  const waarde = useMemo(
    () => ({ selectedTasks, setSelectedTasks, bewaarOnder }),
    [selectedTasks, setSelectedTasks, bewaarOnder],
  );
  return <TaskStateContext value={waarde}>{children}</TaskStateContext>;
}

export function SharedTaskStateProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: SharedTaskStateData;
}) {
  const [state, setShowItems] = useState(value);
  const waarde = useMemo(() => ({ state, setShowItems }), [state]);
  return <SharedTaskStateContext value={waarde}>{children}</SharedTaskStateContext>;
}

const GEEN_GEDEELDE_TAKEN: SharedTaskStateData = {
  showItems: false,
  allTasksPerPlayer: [],
  allTasks: [],
};

export default function TaskProviders({ children }: { children: ReactNode }) {
  return (
    <TaskStateProvider value={[]}>
      <SharedTaskStateProvider value={GEEN_GEDEELDE_TAKEN}>{children}</SharedTaskStateProvider>
    </TaskStateProvider>
  );
}
